/**
 * eventType — the ONLY place the `io.doclm.{domain}[.{sub}].{phase}` grammar is
 * encoded.
 *
 * The backend replaced the four-event wire (`stream` / `chat_meta` / `chat_step` /
 * `error`) with a ~60-type event surface.
 */

export const EVENT_TYPE_PREFIX = 'io.doclm.';

/**
 * Canonical step phase. Wire phases alias into this; `unknown` is a first-class
 * member so an unrecognised phase degrades instead of disappearing.
 */
export const STEP_PHASE = {
  PENDING: 'pending',
  STARTED: 'started',
  DELTA: 'delta',
  BLOCKED: 'blocked',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  UNKNOWN: 'unknown'
} as const;

export type StepPhase = (typeof STEP_PHASE)[keyof typeof STEP_PHASE];

const PHASE_ALIASES: Readonly<Record<string, StepPhase>> = {
  requested: STEP_PHASE.PENDING,
  accepted: STEP_PHASE.PENDING,

  started: STEP_PHASE.STARTED,
  created: STEP_PHASE.STARTED,

  delta: STEP_PHASE.DELTA,
  progress: STEP_PHASE.DELTA,
  updated: STEP_PHASE.DELTA,

  required: STEP_PHASE.BLOCKED,

  completed: STEP_PHASE.COMPLETED,
  finalized: STEP_PHASE.COMPLETED,
  resolved: STEP_PHASE.COMPLETED,

  error: STEP_PHASE.FAILED,

  cancelled: STEP_PHASE.CANCELLED,
  abandoned: STEP_PHASE.CANCELLED
};

const RUN_CONTROL_PHASES: ReadonlySet<string> = new Set([
  'cancellation_requested',
  'suspended',
  'resumed',
  'interrupt'
]);

const CHANNELS: ReadonlySet<string> = new Set(['input', 'output']);

const MODES: ReadonlySet<string> = new Set(['read', 'write']);

export interface ParsedEventType {
  raw: string;
  domain: string;
  channel?: 'input' | 'output';
  mode?: string;
  phase: StepPhase;
  phaseRaw: string;
  prefixed: boolean;
}

/**
 * Parse an `event_type`. Never throws: a malformed or unrecognised string yields a
 * best-effort result with `phase: 'unknown'`, because a frame we can't classify must
 * still be renderable and countable — not dropped.
 */
export function parseEventType(raw: string | undefined): ParsedEventType {
  const value = typeof raw === 'string' ? raw.trim() : '';
  if (value === '') {
    return {
      raw: String(raw ?? ''),
      domain: '',
      phase: STEP_PHASE.UNKNOWN,
      phaseRaw: '',
      prefixed: false
    };
  }

  const prefixed = value.startsWith(EVENT_TYPE_PREFIX);
  const body = prefixed ? value.slice(EVENT_TYPE_PREFIX.length) : value;
  const segments = body.split('.').filter((s) => s !== '');

  if (segments.length < 2) {
    return {
      raw: value,
      domain: segments[0] ?? '',
      phase: STEP_PHASE.UNKNOWN,
      phaseRaw: '',
      prefixed
    };
  }

  const phaseRaw = segments[segments.length - 1];
  const head = segments.slice(0, -1);

  let channel: 'input' | 'output' | undefined;
  let mode: string | undefined;
  let domainParts = head;

  const tail = head[head.length - 1];
  if (head.length >= 2 && CHANNELS.has(tail)) {
    channel = tail as 'input' | 'output';
    domainParts = head.slice(0, -1);
  } else if (head.length >= 2 && MODES.has(tail)) {
    mode = tail;
    domainParts = head.slice(0, -1);
  }

  return {
    raw: value,
    domain: domainParts.join('.'),
    channel,
    mode,
    phase: aliasPhase(phaseRaw),
    phaseRaw,
    prefixed
  };
}

/** Wire phase → canonical phase. Run control phases and anything unrecognised
 * become `unknown` (see [RUN_CONTROL_PHASES]). */
export function aliasPhase(phaseRaw: string): StepPhase {
  if (RUN_CONTROL_PHASES.has(phaseRaw)) {
    return STEP_PHASE.UNKNOWN;
  }
  return PHASE_ALIASES[phaseRaw] ?? STEP_PHASE.UNKNOWN;
}

/**
 * Phase RANK — the ordering guard for out-of-order delivery.
 */
export function phaseRank(phase: StepPhase): number {
  switch (phase) {
    case STEP_PHASE.PENDING:
      return 0;
    case STEP_PHASE.STARTED:
    case STEP_PHASE.DELTA:
    case STEP_PHASE.BLOCKED:
    case STEP_PHASE.UNKNOWN:
      return 1;
    case STEP_PHASE.COMPLETED:
    case STEP_PHASE.FAILED:
    case STEP_PHASE.CANCELLED:
      return 2;
    default:
      return 1;
  }
}

/** Terminal phases — a step in one of these accepts no further transitions. */
export function isTerminalPhase(phase: StepPhase): boolean {
  return phaseRank(phase) === 2;
}

/**
 * Known domains, for readability at call sites. NOT an allowlist — an
 * unrecognised domain is valid input and renders through the fallback. Adding a
 * member here is documentation, not registration.
 */
export const EVENT_DOMAIN = {
  RUN: 'run',
  MESSAGE: 'message',
  TOOL: 'tool',
  RETRIEVAL: 'retrieval',
  ARTIFACT: 'artifact',
  INTERACTION: 'interaction',
  ACTIVITY: 'activity',
  SKILL: 'skill',
  SUBAGENT: 'subagent',
  MEMORY: 'memory',
  CONTEXT_COMPACTION: 'context.compaction',
  USAGE: 'usage'
} as const;
